import React, { useEffect, useState } from "react";
import { FaComment } from "react-icons/fa";
import { BASE_URL } from "../config";
import {
  getScheduleWeekBounds,
  formatWeekStartLabel,
  loadIncludedStaffRequestIds,
} from "../utils/scheduleWeekUtils";

function getAuthToken() {
  return localStorage.getItem("token") || localStorage.getItem("authToken");
}

function formatShiftsLabel(row) {
  const hours = Number(row?.scheduledHours);
  const hoursText = Number.isFinite(hours) ? `${hours}h` : "0h";
  const count = Number(row?.shifts) || 0;
  const shiftWord = count === 1 ? "shift" : "shifts";
  return `${hoursText} (${count} ${shiftWord})`;
}

export default function ScheduleStaffRequestsModal({
  open,
  onClose,
  clientId,
  clientName,
  weekAnchorDate,
  refreshKey = 0,
  includeStaffRequestIds = [],
  onReplicate,
  onView,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  const { shiftDateFrom, shiftDateTo, weekStart } =
    getScheduleWeekBounds(weekAnchorDate) || {};
  const weekLabel = formatWeekStartLabel(weekStart);
  const includeIdsKey = [
    ...new Set([
      ...loadIncludedStaffRequestIds(clientId, shiftDateFrom, shiftDateTo),
      ...(includeStaffRequestIds || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    ]),
  ]
    .map((id) => String(id))
    .join(",");

  useEffect(() => {
    if (!open || !clientId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setRows([]);
      try {
        const params = new URLSearchParams();
        params.set("client_id", String(clientId));
        params.set("all", "1");
        params.set("showAllHistory", "1");
        params.set("limit", "100");
        params.set("page", "1");
        params.set("sortBy", "time");
        params.set("sortDirection", "desc");
        params.set("includeReplicatedFromWeek", "1");
        if (shiftDateFrom) params.set("shiftDateFrom", shiftDateFrom);
        if (shiftDateTo) params.set("shiftDateTo", shiftDateTo);
        if (includeIdsKey) {
          params.set("includeStaffRequestIds", includeIdsKey);
        }

        const token = getAuthToken();
        const res = await fetch(
          `${BASE_URL}/api/jobs/staffingrequests?${params.toString()}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json?.message || "Failed to load staffing requests");
        }
        if (!cancelled) {
          setRows(Array.isArray(json?.data) ? json.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load staffing requests");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, clientId, shiftDateFrom, shiftDateTo, refreshKey, includeIdsKey]);

  if (!open) return null;

  const handleView = (row) => {
    const id = row?.id || row?.staff_request_id;
    if (!id || !onView) return;
    onView(row);
  };

  const handleReplicate = (row) => {
    if (onReplicate) onReplicate(row);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[50350] flex items-center justify-center p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            Staff Requests for {clientName || "Client"} week of {weekLabel}
          </h2>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading && (
            <div className="text-center text-gray-500 py-8">Loading…</div>
          )}
          {!loading && error && (
            <div className="text-center text-red-600 py-8">{error}</div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No staffing requests for this client in this week.
            </div>
          )}
          {!loading && !error && rows.length > 0 && (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Location</th>
                  <th className="px-3 py-2 text-left font-medium">Time</th>
                  <th className="px-3 py-2 text-left font-medium">Shifts</th>
                  <th className="px-3 py-2 text-left font-medium">Notes</th>
                  <th className="px-3 py-2 text-right font-medium" colSpan={2} />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((row) => {
                  const noteText = String(row?.notes || "").trim();
                  return (
                    <tr key={row.id}>
                      <td className="px-3 py-2 align-top">
                        {row.clientLocationName || "—"}
                      </td>
                      <td className="px-3 py-2 align-top whitespace-normal">
                        {row.time || "—"}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {formatShiftsLabel(row)}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {noteText ? (
                          <FaComment
                            className="text-gray-500"
                            title={noteText}
                            aria-label="Has notes"
                          />
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <button
                          type="button"
                          className="px-3 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                          onClick={() => handleView(row)}
                        >
                          VIEW
                        </button>
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <button
                          type="button"
                          className="px-3 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                          onClick={() => handleReplicate(row)}
                        >
                          REPLICATE
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t flex justify-end">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700"
            onClick={onClose}
          >
            DONE
          </button>
        </div>
      </div>
    </div>
  );
}
